import {Box, Button, TextField, Typography, IconButton, MenuItem, Select, FormControl, Switch, Autocomplete, Chip} from "@mui/material";
import MyDialog from "../shared/Dialog";
import {useState, useEffect} from "react";
import {Upload as UploadIcon, Close as CloseIcon, DescriptionOutlined as FileIcon, Videocam as VideoIcon} from "@mui/icons-material";
import {FileUploader} from "react-drag-drop-files";
import {useCompany} from "../../contexts/CompanyContext";
import {useAuth} from "../../contexts/AuthContext";
import {useMessageContext} from "../../contexts/MessageContext";
import {isValidVideoUrl, isValidHttpUrl, getVideoType, isValidYouTubeUrl, getYouTubeThumbnailUrl, MAX_VIDEO_UPLOAD_MB, getVideoTooLargeMessage} from "../../utils/videoUtils";
import VideoThumbnailExtractor from "../guides/VideoThumbnailExtractor";
import {generateThumbnailFilename} from "../../utils/videoThumbnailUtils";
import RecipeNutritionFields from "./RecipeNutritionFields";
import RichTextEditor, {isRichTextEmpty} from "./RichTextEditor";
import {RTL} from "../../cache/CacheRtl";
import {uploadVideoDirect} from "../../services/MediaUploadService";
import UploadProgressIndicator from "../common/UploadProgressIndicator";

const parseNumOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

const parseIntOrNull = (v) => {
    const n = parseNumOrNull(v);
    return n === null ? null : Math.round(n);
};

// Best-effort content type inference for items whose backend payload predates the
// explicit `contentType` field. Only used when content types are enabled (Recipes).
const inferContentType = (item, contentTypes, fallback) => {
    if (!item) return fallback;
    if (item.contentType) return item.contentType;
    const hasVideo = !!item.videoUrl || !!item.videoFilename;
    const hasText = !!item.content;
    let inferred;
    if (item.pdfUrl) inferred = "PDF";
    else if (hasVideo && hasText) inferred = "TEXT_VIDEO";
    else if (hasVideo) inferred = "VIDEO";
    else if (hasText) inferred = "TEXT";
    const allowed = (contentTypes || []).some(ct => ct.value === inferred);
    return allowed ? inferred : fallback;
};

const ResourceDialog = ({config, isOpen, close, setReload, editItem = null, viewMode = false}) => {
    const {service, categoryService, tagService, labels} = config;
    const dialogLabels = labels.dialog;
    const contentTypes = config.contentTypes;
    const hasContentTypes = Array.isArray(contentTypes) && contentTypes.length > 0;
    const videoUploadMaxSizeMb = config.videoUploadMaxSizeMb || MAX_VIDEO_UPLOAD_MB;
    const showDescription = config.showDescription !== false;
    const defaultContentType = config.defaultContentType || contentTypes?.[0]?.value || "";

    const hasNutrition = !!config.hasNutrition;
    // Recipes use a rich-text (HTML) editor for the long-form body; Guides keep
    // the plain multiline textarea.
    const richTextContent = !!config.richTextContent;
    const nutritionMacros = config.nutritionMacros || [];
    // A blank measurement unit: name/amount/calories plus one empty field per macro.
    const emptyUnit = () => nutritionMacros.reduce(
        (acc, m) => ({...acc, [m.key]: ""}),
        {name: "", amount: "", calories: ""}
    );

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [contentType, setContentType] = useState(defaultContentType);
    const [content, setContent] = useState('');
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    // Video upload states
    const [isVideoUploadMode, setIsVideoUploadMode] = useState(false);
    const [videoFile, setVideoFile] = useState(null);
    const [videoFileName, setVideoFileName] = useState('');
    const [uploadedVideoUrl, setUploadedVideoUrl] = useState(null);
    const [videoUploadState, setVideoUploadState] = useState({
        phase: 'idle', uploadProgress: 0, compressionProgress: 0, error: null
    });
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [existingVideoUrl, setExistingVideoUrl] = useState(null); // For loading existing S3 videos
    const [videoThumbnailTime, setVideoThumbnailTime] = useState(0); // Track slider position for thumbnail

    // Cover image (writes to the same thumbnailUrl field). User uploads take precedence
    // over auto-derived video thumbnails.
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [removeCoverImage, setRemoveCoverImage] = useState(false);

    const [categoryId, setCategoryId] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [tags, setTags] = useState([]);

    // Nutrition (Recipes only). Values kept as strings for controlled inputs.
    const [units, setUnits] = useState([]);
    const [initialNutrition, setInitialNutrition] = useState("");

    // Normalizes the current nutrition inputs into the exact payload sent to the server.
    // Macros (protein/carbs/fat/fiber) live on each measurement unit.
    const computeNutrition = (unitsArg) => {
        const measurementUnits = (unitsArg || [])
            .filter((u) => (u.name || "").trim())
            .map((u) => {
                const unit = {name: u.name.trim(), amount: parseNumOrNull(u.amount), calories: parseIntOrNull(u.calories)};
                nutritionMacros.forEach((m) => { unit[m.key] = parseNumOrNull(u[m.key]); });
                return unit;
            });
        return {measurementUnits};
    };

    const {company, getCompaniesList} = useCompany();
    const {isSuperAdmin} = useAuth();
    const {notificationHandler} = useMessageContext();

    // Track initial values for edit mode
    const [initialTitle, setInitialTitle] = useState('');
    const [initialDescription, setInitialDescription] = useState('');
    const [initialVideoUrl, setInitialVideoUrl] = useState('');
    const [initialLinkUrl, setInitialLinkUrl] = useState('');
    const [initialPdfFileName, setInitialPdfFileName] = useState('');
    const [initialVideoFileName, setInitialVideoFileName] = useState('');
    const [initialThumbnailUrl, setInitialThumbnailUrl] = useState('');
    const [initialContent, setInitialContent] = useState('');
    const [initialContentType, setInitialContentType] = useState('');

    const isEditMode = !!editItem && !viewMode;
    const isViewMode = viewMode;

    // Which sections are relevant for the current content type.
    // When content types are disabled (Guides) everything media-related is shown,
    // preserving the original behavior.
    const showPdf = !hasContentTypes || contentType === "PDF";
    const showVideo = !hasContentTypes || contentType === "VIDEO" || contentType === "TEXT_VIDEO";
    const showText = hasContentTypes && (contentType === "TEXT" || contentType === "TEXT_VIDEO");
    // Cover image is optional and offered for every content type (PDF, Text, Video,
    // Text+Video) as well as Guides — so a recipe can always have a list thumbnail.
    const showCover = true;

    useEffect(() => {
        if (editItem) {
            const itemTitle = editItem.title || '';
            const itemDesc = editItem.description || '';
            const itemVideo = editItem.videoUrl || '';
            const itemLink = editItem.linkUrl || '';
            const itemVideoFileName = editItem.videoFilename || '';

            setTitle(itemTitle);
            setDescription(itemDesc);

            const itemContent = editItem.content || '';
            const itemContentType = inferContentType(editItem, contentTypes, defaultContentType);
            setContent(itemContent);
            setContentType(itemContentType);

            // Extract filename from pdfUrl if pdfFileName is not provided
            let itemPdfFileName = '';
            if (editItem.pdfUrl) {
                itemPdfFileName = editItem.pdfFileName || editItem.pdfUrl.split('/').pop() || 'קובץ PDF';
                setPdfFileName(itemPdfFileName);
            } else {
                setPdfFileName('');
            }

            // Check if item has uploaded video file or S3 URL (which means it was uploaded)
            const videoType = getVideoType(itemVideo);
            const isS3Video = videoType === 's3';

            if (itemVideoFileName || isS3Video) {
                setIsVideoUploadMode(true);
                // Use existing filename or extract from S3 URL
                const displayFileName = itemVideoFileName || (isS3Video ? itemVideo.split('/').pop() || 'סרטון שהועלה' : '');
                setVideoFileName(displayFileName);
                setVideoUrl('');
                if (editItem.thumbnailUrl) {
                    setThumbnailPreview(editItem.thumbnailUrl);
                }
                // Store the existing video URL for the thumbnail extractor
                if (isS3Video) {
                    setExistingVideoUrl(itemVideo);
                }
            } else {
                setIsVideoUploadMode(false);
                setVideoUrl(itemVideo);
                setVideoFileName('');
                setExistingVideoUrl(null);
            }

            // Set company for edit mode
            if (editItem.companyId) {
                setSelectedCompanyId(editItem.companyId);
            }

            // Optional external website link.
            setLinkUrl(itemLink);

            // Set category for edit mode
            setCategoryId(editItem.categoryId || "");
            // Set assigned tags for edit mode ([{id, name}] from the item DTO)
            setSelectedTags(editItem.tags || []);

            // Initialize cover image preview for any item that already has a thumbnailUrl,
            // regardless of media type. Inside the video-upload branch above we also set
            // thumbnailPreview for legacy video-thumbnail UX; this drives the dedicated
            // Cover Image section instead.
            setCoverImageFile(null);
            setCoverImagePreview(editItem.thumbnailUrl || null);
            setRemoveCoverImage(false);

            // Store initial values for change detection
            setInitialTitle(itemTitle);
            setInitialDescription(itemDesc);
            setInitialVideoUrl(itemVideo);
            setInitialLinkUrl(itemLink);
            setInitialPdfFileName(itemPdfFileName);
            setInitialVideoFileName(itemVideoFileName);
            setInitialThumbnailUrl(editItem.thumbnailUrl || '');
            setInitialContent(itemContent);
            setInitialContentType(itemContentType);

            if (hasNutrition) {
                const loadedUnits = (editItem.measurementUnits || []).map((u) => {
                    const unit = {
                        name: u.name ?? "",
                        amount: u.amount != null ? String(u.amount) : "",
                        calories: u.calories != null ? String(u.calories) : ""
                    };
                    nutritionMacros.forEach((m) => {
                        unit[m.key] = u[m.key] != null ? String(u[m.key]) : "";
                    });
                    return unit;
                });
                const seededUnits = loadedUnits.length ? loadedUnits : [emptyUnit()];
                setUnits(seededUnits);
                setInitialNutrition(JSON.stringify(computeNutrition(seededUnits)));
            }
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editItem, isOpen]);

    // Fetch categories and tags when dialog opens or company changes
    useEffect(() => {
        if (!isOpen) return;
        const compId = isSuperAdmin() ? selectedCompanyId : company?.id;
        if (!compId) { setCategories([]); setTags([]); return; }
        categoryService.getCategories(compId)
            .then(res => setCategories(res.data || []))
            .catch(() => setCategories([]));
        if (tagService) {
            tagService.getTags(compId)
                .then(res => setTags(res.data || []))
                .catch(() => setTags([]));
        }
    }, [isOpen, selectedCompanyId, company, isSuperAdmin, categoryService, tagService]);

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setContent('');
        setContentType(defaultContentType);
        setPdfFile(null);
        setPdfFileName('');
        setVideoUrl('');
        setLinkUrl('');
        setInitialLinkUrl('');
        setSelectedCompanyId('');
        setIsVideoUploadMode(false);
        setVideoFile(null);
        setVideoFileName('');
        setUploadedVideoUrl(null);
        setVideoUploadState({phase: 'idle', uploadProgress: 0, compressionProgress: 0, error: null});
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setExistingVideoUrl(null);
        setVideoThumbnailTime(0);
        setCategoryId("");
        setSelectedTags([]);
        setCoverImageFile(null);
        setCoverImagePreview(null);
        setRemoveCoverImage(false);

        if (hasNutrition) {
            const seededUnits = [emptyUnit()];
            setUnits(seededUnits);
            setInitialNutrition(JSON.stringify(computeNutrition(seededUnits)));
        }
    };

    const handleCoverImageChange = (file) => {
        if (!file) return;
        setCoverImageFile(file);
        setCoverImagePreview(URL.createObjectURL(file));
        setRemoveCoverImage(false);
    };

    const handleRemoveCoverImage = () => {
        setCoverImageFile(null);
        setCoverImagePreview(null);
        setRemoveCoverImage(true);
    };

    const handleFileChange = (file) => {
        if (file) {
            setPdfFile(file);
            setPdfFileName(file.name);
        }
    };

    const handleRemoveFile = () => {
        setPdfFile(null);
        setPdfFileName('');
    };

    const handleVideoFileChange = async (file) => {
        if (file) {
            setVideoFile(file);
            setVideoFileName(file.name);
            setUploadedVideoUrl(null);
            // Reset thumbnail when new video is selected - will be auto-extracted
            setThumbnailFile(null);
            setThumbnailPreview(null);
            // Clear existing URL since we have a new file
            setExistingVideoUrl(null);

            setVideoUploadState({phase: 'uploading', uploadProgress: 0, compressionProgress: 0, error: null});
            try {
                const cdnUrl = await uploadVideoDirect(
                    file,
                    (uploadProgress) => setVideoUploadState((previous) => ({
                        ...previous, phase: 'uploading', uploadProgress
                    })),
                    (compressionProgress) => setVideoUploadState((previous) => ({
                        ...previous, phase: 'compressing', uploadProgress: 100, compressionProgress
                    }))
                );
                setUploadedVideoUrl(cdnUrl);
                setVideoUploadState({phase: 'complete', uploadProgress: 100, compressionProgress: 100, error: null});
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                setVideoUploadState({phase: 'error', uploadProgress: 0, compressionProgress: 0, error: message});
            }
        }
    };

    const handleThumbnailExtracted = (blob, previewUrl, time) => {
        if (blob) {
            // Convert blob to File for upload compatibility
            const thumbnailFileName = generateThumbnailFilename(videoFileName || 'video');
            const file = new File([blob], thumbnailFileName, { type: 'image/jpeg' });
            setThumbnailFile(file);
            setThumbnailPreview(previewUrl);
            if (time !== undefined) {
                setVideoThumbnailTime(time);
            }
        } else {
            setThumbnailFile(null);
            setThumbnailPreview(null);
        }
    };

    const handleVideoUrlChange = (e) => {
        const url = e.target.value;
        setVideoUrl(url);

        // Auto-set thumbnail from YouTube URL
        if (isValidYouTubeUrl(url) && url.trim()) {
            const thumbnailUrl = getYouTubeThumbnailUrl(url);
            if (thumbnailUrl) {
                setThumbnailPreview(thumbnailUrl);
                // Clear any file-based thumbnail since we're using URL
                setThumbnailFile(null);
            }
        }
    };

    const handleRemoveVideoFile = () => {
        setVideoFile(null);
        setVideoFileName('');
        setUploadedVideoUrl(null);
        setVideoUploadState({phase: 'idle', uploadProgress: 0, compressionProgress: 0, error: null});
        setExistingVideoUrl(null);
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setVideoThumbnailTime(0);
    };

    const hasFormChanged = () => {
        if (!isEditMode) return true;

        const pdfRemoved = initialPdfFileName && !pdfFileName && !pdfFile;
        const videoFileChanged = !!videoFile || (initialVideoFileName && !videoFileName);
        // Check if thumbnail will change due to YouTube URL
        const newYouTubeThumbnail = isValidYouTubeUrl(videoUrl.trim()) ? getYouTubeThumbnailUrl(videoUrl.trim()) : null;
        const thumbnailWillChange = newYouTubeThumbnail !== initialThumbnailUrl;
        const coverChanged = !!coverImageFile || (removeCoverImage && !!initialThumbnailUrl);
        const contentChanged = content.trim() !== initialContent.trim();
        const contentTypeChanged = contentType !== initialContentType;
        const nutritionChanged = hasNutrition &&
            JSON.stringify(computeNutrition(units)) !== initialNutrition;
        // Category is mandatory; allow saving an edit that only (re)assigns the category — e.g. an
        // existing item that predates the mandatory rule and needs a category set.
        const categoryChanged = String(categoryId || "") !== String(editItem?.categoryId || "");
        return (
            categoryChanged ||
            title.trim() !== initialTitle.trim() ||
            description.trim() !== initialDescription.trim() ||
            videoUrl.trim() !== initialVideoUrl.trim() ||
            linkUrl.trim() !== initialLinkUrl.trim() ||
            !!pdfFile ||
            pdfRemoved ||
            videoFileChanged ||
            !!thumbnailFile ||
            thumbnailWillChange ||
            coverChanged ||
            contentChanged ||
            contentTypeChanged ||
            nutritionChanged
        );
    };

    const isFormValid = () => {
        const hasTitle = title.trim().length > 0;
        // Description can be made optional per-resource via config (default: required).
        const descriptionRequired = config.descriptionRequired !== false;
        const hasDescription = !descriptionRequired || description.trim().length > 0;
        const hasFile = isEditMode ? (pdfFileName || pdfFile) : pdfFile;
        const hasVideoUrl = videoUrl.trim().length > 0 && isValidVideoUrl(videoUrl);
        const hasVideoFile = videoFile
            ? !!uploadedVideoUrl
            : (isEditMode ? !!videoFileName : false);
        const hasVideo = isVideoUploadMode ? hasVideoFile : hasVideoUrl;
        const hasText = richTextContent ? !isRichTextEmpty(content) : content.trim().length > 0;
        const hasCompany = isSuperAdmin() ? (isEditMode ? true : selectedCompanyId) : true;
        // Only enforce a valid video URL when the video section is actually in use.
        const validVideo = !showVideo ? true : (isVideoUploadMode ? true : isValidVideoUrl(videoUrl));
        // The external link is optional; isValidHttpUrl treats empty as valid.
        const validLink = isValidHttpUrl(linkUrl);
        const hasLink = linkUrl.trim().length > 0 && isValidHttpUrl(linkUrl);
        // Recipes are auto-added to the food database, so at least one serving unit is
        // required and every field on each unit must be filled. Guides are unaffected.
        const isFieldFilled = (v) => v !== "" && v !== null && v !== undefined && String(v).trim().length > 0;
        const isUnitComplete = (u) =>
            isFieldFilled(u.name) &&
            isFieldFilled(u.amount) &&
            isFieldFilled(u.calories) &&
            nutritionMacros.every((m) => m.optional || isFieldFilled(u[m.key]));
        const hasServingUnit = !hasNutrition || (units.length > 0 && units.every(isUnitComplete));
        // Category is mandatory: a guide/recipe can never be without one. Requires a selected category
        // (which also blocks save when the company has no categories yet — see the notice in the dialog).
        const hasCategory = !!categoryId;

        // Required media/content depends on the content type. When content types are
        // disabled (Guides), a guide's body may be a PDF, a video, OR just an external link.
        //
        // The link used to be excluded here, so a guide whose whole point IS the link (an article
        // on another site) could never be saved — the button stayed greyed out with every visible
        // field filled and nothing explaining why. Nothing downstream required a PDF/video: the
        // server only enforces a category, and the mobile GuideDetailScreen renders linkUrl on its
        // own as an "open link" button. The admin form was simply stricter than the platform.
        //
        // Scoped to the no-content-type branch on purpose: where a content type IS chosen the user
        // has explicitly asked for a PDF/VIDEO/TEXT body, so a link must not satisfy it.
        let hasRequiredContent;
        if (!hasContentTypes) {
            hasRequiredContent = hasFile || hasVideo || hasLink;
        } else if (contentType === "PDF") {
            hasRequiredContent = hasFile;
        } else if (contentType === "VIDEO") {
            hasRequiredContent = hasVideo;
        } else if (contentType === "TEXT") {
            hasRequiredContent = hasText;
        } else if (contentType === "TEXT_VIDEO") {
            hasRequiredContent = hasText && hasVideo;
        } else {
            hasRequiredContent = false;
        }

        return hasTitle && hasDescription && hasRequiredContent && hasCompany && hasCategory && hasFormChanged() && validVideo && validLink && hasServingUnit;
    };

    const handleSave = async () => {
        if (!isFormValid()) {
            return;
        }

        setLoading(true);

        try {
            if (isEditMode) {
                const itemData = {};

                if (title.trim() !== initialTitle.trim()) {
                    itemData.title = title.trim();
                }

                if (description.trim() !== initialDescription.trim()) {
                    itemData.description = description.trim();
                }

                // Only send the link when it actually changed. An empty string is sent
                // deliberately (not omitted) — that is what clears the link server-side.
                if (linkUrl.trim() !== initialLinkUrl.trim()) {
                    itemData.linkUrl = linkUrl.trim();
                }

                // Handle video based on mode
                if (isVideoUploadMode) {
                    if (videoFile) {
                        itemData.videoUrl = uploadedVideoUrl;
                    }
                    if (thumbnailFile) {
                        itemData.thumbnail = thumbnailFile;
                    } else if (initialThumbnailUrl && !thumbnailPreview) {
                        // Thumbnail was removed
                        itemData.removeThumbnail = true;
                    }
                } else {
                    if (videoUrl.trim() !== initialVideoUrl.trim()) {
                        itemData.videoUrl = videoUrl.trim() || null;
                    }
                    // Clear video file if switching from upload mode to URL mode
                    if (initialVideoFileName) {
                        itemData.removeVideo = true;
                    }
                    // Set YouTube thumbnail or clear existing thumbnail
                    if (isValidYouTubeUrl(videoUrl.trim()) && videoUrl.trim()) {
                        itemData.thumbnailUrl = getYouTubeThumbnailUrl(videoUrl.trim());
                        // Don't set removeThumbnail when we have a new thumbnailUrl
                    } else if (initialThumbnailUrl) {
                        // Clear thumbnail if switching to non-YouTube URL
                        itemData.removeThumbnail = true;
                    }
                }

                if (pdfFile) {
                    itemData.pdfFile = pdfFile;
                    itemData.pdfFileName = pdfFileName;
                } else if (initialPdfFileName && !pdfFileName) {
                    itemData.removePdf = true;
                }

                // Cover image takes precedence over video-derived thumbnails.
                // Apply last so it overrides anything the video branch set.
                if (coverImageFile) {
                    itemData.thumbnail = coverImageFile;
                    delete itemData.thumbnailUrl;
                    delete itemData.removeThumbnail;
                } else if (removeCoverImage) {
                    delete itemData.thumbnail;
                    delete itemData.thumbnailUrl;
                    itemData.removeThumbnail = true;
                } else if (coverImagePreview && initialThumbnailUrl && coverImagePreview === initialThumbnailUrl) {
                    // User kept the existing cover untouched — don't let the video branch
                    // overwrite it with a new YouTube auto-thumb or clear it.
                    delete itemData.thumbnail;
                    delete itemData.thumbnailUrl;
                    delete itemData.removeThumbnail;
                }

                itemData.categoryId = categoryId || null;
                itemData.tagIds = selectedTags.map(tg => tg.id);

                // Content-type aware fields + cleanup of media that no longer belongs
                // to the selected content type (Recipes only).
                if (hasContentTypes) {
                    itemData.contentType = contentType;

                    if (showText) {
                        itemData.content = richTextContent
                            ? (isRichTextEmpty(content) ? null : content)
                            : (content.trim() || null);
                    } else if (initialContent) {
                        itemData.content = null;
                    }

                    if (!showPdf && initialPdfFileName) {
                        itemData.removePdf = true;
                        delete itemData.pdfFile;
                        delete itemData.pdfFileName;
                    }

                    if (!showVideo) {
                        if (initialVideoFileName) itemData.removeVideo = true;
                        if (initialVideoUrl) itemData.videoUrl = null;
                    }

                    if (!showCover && initialThumbnailUrl) {
                        itemData.removeThumbnail = true;
                        delete itemData.thumbnail;
                        delete itemData.thumbnailUrl;
                    }
                }

                if (hasNutrition) {
                    Object.assign(itemData, computeNutrition(units));
                }

                await service.update(editItem.id, itemData);
            } else {
                const companyId = isSuperAdmin() ? selectedCompanyId : company?.id;

                const itemData = {
                    title: title.trim(),
                    description: description.trim(),
                    companyId: companyId,
                    categoryId: categoryId || null,
                    tagIds: selectedTags.map(tg => tg.id),
                    linkUrl: linkUrl.trim() || null
                };

                if (hasContentTypes) {
                    itemData.contentType = contentType;
                }

                if (showText) {
                    itemData.content = richTextContent
                        ? (isRichTextEmpty(content) ? null : content)
                        : (content.trim() || null);
                }

                // Handle video based on mode
                if (showVideo) {
                    if (isVideoUploadMode) {
                        if (videoFile) {
                            itemData.videoUrl = uploadedVideoUrl;
                        }
                        if (thumbnailFile) {
                            itemData.thumbnail = thumbnailFile;
                        }
                    } else {
                        itemData.videoUrl = videoUrl.trim() || null;
                        // Set YouTube thumbnail URL if available
                        if (isValidYouTubeUrl(videoUrl.trim())) {
                            itemData.thumbnailUrl = getYouTubeThumbnailUrl(videoUrl.trim());
                        }
                    }
                }

                if (showPdf && pdfFile) {
                    itemData.pdfFile = pdfFile;
                    itemData.pdfFileName = pdfFileName;
                }

                // Cover image takes precedence over any video-derived thumbnail.
                if (showCover && coverImageFile) {
                    itemData.thumbnail = coverImageFile;
                    delete itemData.thumbnailUrl;
                }

                if (hasNutrition) {
                    Object.assign(itemData, computeNutrition(units));
                }

                await service.save(itemData);
            }

            close();
            resetForm();
            setReload();

            // Standard success popup. On create, recipes are also mirrored into the food
            // database, so the message (config-driven) reflects that.
            const successMessage = isEditMode ? labels.editSuccessMessage : labels.createSuccessMessage;
            if (successMessage && notificationHandler?.success) {
                notificationHandler.success(successMessage);
            }
        } catch (error) {
            console.error('Error saving resource:', error);
            // Prefer a specific reason from the server (e.g. oversized upload) over the generic message.
            const serverMessage = error?.response?.data?.errorMessage;
            alert(serverMessage || dialogLabels.saveError);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        close();
        if (!isEditMode && !isViewMode) {
            resetForm();
        }
    };

    const getDialogTitle = () => {
        if (isViewMode) return dialogLabels.viewTitle;
        if (isEditMode) return dialogLabels.editTitle;
        return dialogLabels.createTitle;
    };

    return (
        <MyDialog
            isOpen={isOpen}
            close={handleClose}
            title={getDialogTitle()}
            showCloseButton={true}
            isTitleCentered={false}
            disableEnforceFocus={richTextContent}
            actions={
                !isViewMode && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!isFormValid() || loading}
                        sx={{
                            backgroundColor: "#57837E",
                            "&:hover": {
                                backgroundColor: "#466963"
                            },
                            "&:disabled": {
                                backgroundColor: "#E2E8F0"
                            }
                        }}
                    >
                        {loading ? 'שומר...' : dialogLabels.saveButton}
                    </Button>
                )
            }
        >
            <Box sx={{padding: "20px", display: "flex", flexDirection: "column", gap: "20px"}}>
                {isSuperAdmin() && !isEditMode && (
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#4F535C",
                            marginBottom: "8px",
                            textAlign: "right"
                        }}>
                            חברה <span style={{color: '#F16063'}}>*</span>
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={selectedCompanyId}
                                onChange={(e) => setSelectedCompanyId(e.target.value)}
                                displayEmpty
                                sx={{
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#57837E',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#57837E',
                                    }
                                }}
                            >
                                <MenuItem value="" disabled>
                                    בחר חברה
                                </MenuItem>
                                {getCompaniesList().map((comp) => (
                                    <MenuItem key={comp.id} value={comp.id}>
                                        {comp.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Box>
                    <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#4F535C",
                        marginBottom: "8px",
                        textAlign: "right"
                    }}>
                        {dialogLabels.titleLabel || "כותרת"}
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder={dialogLabels.titlePlaceholder}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={isViewMode}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F3F3F5',
                                '&:hover fieldset': {
                                    borderColor: '#57837E',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#57837E',
                                }
                            }
                        }}
                    />
                </Box>

                {showDescription && (
                <Box>
                    <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#4F535C",
                        marginBottom: "8px",
                        textAlign: "right"
                    }}>
                        תיאור קצר 
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="הזינו תיאור קצר (שורה-שתיים)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isViewMode}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F3F3F5',
                                '&:hover fieldset': {
                                    borderColor: '#57837E',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#57837E',
                                }
                            }
                        }}
                    />
                </Box>
                )}

                {/* Optional external website link. Deliberately outside the video/PDF sections:
                    it is independent of content type, so it shows for PDF and text items too. */}
                <Box>
                    <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#4F535C",
                        marginBottom: "8px",
                        textAlign: "right"
                    }}>
                        קישור לאתר חיצוני (אופציונלי)
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="https://example.com"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        disabled={isViewMode}
                        error={linkUrl.trim().length > 0 && !isValidHttpUrl(linkUrl)}
                        helperText={linkUrl.trim().length > 0 && !isValidHttpUrl(linkUrl)
                            ? "הזן קישור תקין המתחיל ב-http:// או https://"
                            : ""}
                        FormHelperTextProps={{sx: {textAlign: 'right'}}}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F3F3F5',
                                '&:hover fieldset': {
                                    borderColor: '#57837E',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#57837E',
                                }
                            }
                        }}
                    />
                </Box>

                {/* Content type selector (Recipes) — 2x2 card grid */}
                {hasContentTypes && (
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#4F535C",
                            marginBottom: "8px",
                            textAlign: "right"
                        }}>
                            {labels.contentTypeLabel} <span style={{color: '#F16063'}}>*</span>
                        </Typography>
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "12px"
                        }}>
                            {contentTypes.map((ct) => {
                                const selected = contentType === ct.value;
                                const CtIcon = ct.icon;
                                return (
                                    <Box
                                        key={ct.value}
                                        role="button"
                                        aria-pressed={selected}
                                        onClick={() => { if (!isViewMode) setContentType(ct.value); }}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "6px",
                                            minHeight: "84px",
                                            padding: "16px 12px",
                                            borderRadius: "10px",
                                            border: selected ? "2px solid #57837E" : "1px solid #E2E8F0",
                                            backgroundColor: selected ? "#E9F0EC" : "#FFFFFF",
                                            cursor: isViewMode ? "default" : "pointer",
                                            transition: "border-color .15s ease, background-color .15s ease",
                                            opacity: isViewMode && !selected ? 0.6 : 1,
                                            "&:hover": !isViewMode ? {
                                                borderColor: "#57837E",
                                                backgroundColor: selected ? "#E9F0EC" : "#F8FAFC"
                                            } : {}
                                        }}
                                    >
                                        {CtIcon && (
                                            <CtIcon sx={{
                                                fontSize: "26px",
                                                color: selected ? "#57837E" : "#94A3B8"
                                            }}/>
                                        )}
                                        <Typography sx={{
                                            fontSize: "14px",
                                            fontWeight: selected ? 600 : 500,
                                            color: selected ? "#57837E" : "#4F535C"
                                        }}>
                                            {ct.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {/* Category dropdown — mandatory: a guide/recipe can't be saved without a category. */}
                <Box>
                    <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#4F535C",
                        marginBottom: "8px",
                        textAlign: "right"
                    }}>
                        קטגוריה <span style={{color: '#F16063'}}>*</span>
                    </Typography>
                    {categories.length > 0 ? (
                        <FormControl fullWidth>
                            <Select
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value)}
                                displayEmpty
                                disabled={isViewMode}
                                sx={{
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#57837E',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#57837E',
                                    }
                                }}
                            >
                                <MenuItem value="" disabled>בחירת קטגוריה</MenuItem>
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        // No categories yet: category is mandatory, so block the save and tell the admin
                        // to create a category first (in the Categories tab).
                        <Typography sx={{fontSize: "13px", color: '#F16063', textAlign: "right"}}>
                            יש ליצור קטגוריה בלשונית "קטגוריות" לפני הוספת פריט
                        </Typography>
                    )}
                </Box>

                {/* Tags — assign one or more (multi-select chips) */}
                {tagService && tags.length > 0 && (
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#4F535C",
                            marginBottom: "8px",
                            textAlign: "right"
                        }}>
                            {labels.tagsLabel || "תגיות"}
                        </Typography>
                        <Autocomplete
                            multiple
                            options={tags}
                            value={selectedTags}
                            onChange={(_, newValue) => setSelectedTags(newValue)}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disabled={isViewMode}
                            sx={{direction: "rtl"}}
                            renderTags={(value, getTagProps) => (
                                <RTL>
                                    {value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({index})}
                                            key={option.id}
                                            label={option.name}
                                            size="small"
                                            sx={{backgroundColor: "rgba(87, 131, 126, 0.12)", color: "#1E293B"}}
                                        />
                                    ))}
                                </RTL>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={selectedTags.length ? "" : "בחרו תגיות..."}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {direction: "rtl"},
                                        '& .MuiOutlinedInput-input': {textAlign: "right"},
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#57837E'},
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#57837E'},
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}

                {/* Long-form recipe text (Recipes: Text / Text+Video) */}
                {showText && (
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#4F535C",
                            marginBottom: "8px",
                            textAlign: "right"
                        }}>
                            {labels.textBodyLabel} <span style={{color: '#F16063'}}>*</span>
                        </Typography>
                        {richTextContent ? (
                            <RichTextEditor
                                value={content}
                                onChange={setContent}
                                placeholder={labels.textBodyPlaceholder}
                                disabled={isViewMode}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                multiline
                                rows={8}
                                placeholder={labels.textBodyPlaceholder}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                disabled={isViewMode}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#F3F3F5',
                                        '&:hover fieldset': {
                                            borderColor: '#57837E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#57837E',
                                        }
                                    }
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* Cover Image — shown on the mobile list */}
                {showCover && (
                <Box>
                    <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#4F535C",
                        marginBottom: "8px",
                        textAlign: "right"
                    }}>
                        תמונת כיסוי
                    </Typography>

                    <Box sx={{display: "flex", flexDirection: "row-reverse", alignItems: "flex-start", gap: "16px"}}>
                        <Box sx={{ flex: 1, position: "relative", pointerEvents: isViewMode ? "none" : "auto", opacity: isViewMode ? 0.7 : 1 }}>
                            <FileUploader
                                handleChange={handleCoverImageChange}
                                name="coverImage"
                                types={["JPG", "JPEG", "PNG", "WEBP"]}
                                maxSize={10}
                                disabled={isViewMode}
                                onTypeError={() => alert('אנא בחר קובץ תמונה בפורמט נתמך (JPG, PNG, WEBP)')}
                                onSizeError={() => alert('הקובץ גדול מדי. מקסימום 10MB')}
                            >
                                <Box
                                    sx={{
                                        border: "2px dashed #E2E8F0",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        cursor: isViewMode ? "default" : "pointer",
                                        backgroundColor: "#FAFAFA",
                                        "&:hover": !isViewMode ? {
                                            borderColor: "#57837E",
                                            backgroundColor: "#F8FAFC"
                                        } : {}
                                    }}
                                >
                                    <Box sx={{padding: "30px 20px", textAlign: "center"}}>
                                        <UploadIcon sx={{fontSize: "40px", color: "#94A3B8", marginBottom: "8px"}}/>
                                        <Typography sx={{fontSize: "14px", color: "#4F535C", marginBottom: "4px"}}>
                                            גררו ושחררו תמונת כיסוי כאן, או
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            disabled={isViewMode}
                                            sx={{
                                                color: "#4F535C",
                                                borderColor: "#E2E8F0",
                                                backgroundColor: "#FFFFFF",
                                                "&:hover": {
                                                    borderColor: "#57837E",
                                                    backgroundColor: "#F8FAFC"
                                                }
                                            }}
                                        >
                                            בחרו קובץ
                                        </Button>
                                    </Box>
                                </Box>
                            </FileUploader>
                        </Box>

                        {coverImagePreview && (
                            <Box sx={{
                                width: "150px",
                                height: "150px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #E2E8F0",
                                position: "relative",
                                flexShrink: 0
                            }}>
                                <img
                                    src={coverImagePreview}
                                    alt="cover preview"
                                    style={{width: "100%", height: "100%", objectFit: "cover"}}
                                />
                                {!isViewMode && (
                                    <IconButton
                                        size="small"
                                        onClick={handleRemoveCoverImage}
                                        sx={{
                                            position: "absolute",
                                            top: "4px",
                                            right: "4px",
                                            backgroundColor: "rgba(255,255,255,0.9)",
                                            "&:hover": { backgroundColor: "#FFFFFF" }
                                        }}
                                    >
                                        <CloseIcon fontSize="small"/>
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
                )}

                {showVideo && (
                <Box>
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px"}}>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#4F535C",
                            textAlign: "right"
                        }}>
                            סרטון
                        </Typography>
                        {!isViewMode && (
                            <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                <Typography sx={{fontSize: "13px", color: !isVideoUploadMode ? "#94A3B8" : "#57837E"}}>
                                    העלאת סרטון
                                </Typography>

                                <Switch
                                    checked={isVideoUploadMode}
                                    onChange={(e) => {
                                        setIsVideoUploadMode(e.target.checked);
                                        // Don't reset data - just switch the view mode
                                        // Data will only be cleared when user explicitly edits/removes it
                                    }}
                                    size="small"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#57837E',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#57837E',
                                        },
                                    }}
                                />
                                <Typography sx={{fontSize: "13px", color: !isVideoUploadMode ? "#57837E" : "#94A3B8"}}>
                                    קישור לסרטון
                                </Typography>

                            </Box>
                        )}
                    </Box>

                    {!isVideoUploadMode ? (
                        <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                            <TextField
                                fullWidth
                                placeholder="https://example.com/video.mp4"
                                value={videoUrl}
                                onChange={handleVideoUrlChange}
                                disabled={isViewMode}
                                error={videoUrl.trim().length > 0 && !isValidVideoUrl(videoUrl)}
                                helperText={videoUrl.trim().length > 0 && !isValidVideoUrl(videoUrl) ? "הזן קישור תקין לסרטון" : ""}
                                FormHelperTextProps={{ sx: { textAlign: 'right' } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#F3F3F5',
                                        '&:hover fieldset': {
                                            borderColor: '#57837E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#57837E',
                                        }
                                    }
                                }}
                            />
                            {/* YouTube Thumbnail Preview */}
                            {thumbnailPreview && isValidYouTubeUrl(videoUrl) && (
                                <Box>
                                    <Typography sx={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: "#6B7280",
                                        marginBottom: "8px",
                                        textAlign: "right"
                                    }}>
                                        תמונת כיסוי (מ-YouTube)
                                    </Typography>
                                    <Box sx={{
                                        width: '150px',
                                        height: '150px',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        border: '1px solid #E2E8F0'
                                    }}>
                                        <img
                                            src={thumbnailPreview}
                                            alt="YouTube thumbnail"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                            {/* Video File Upload */}
                            <Box sx={{ position: "relative", pointerEvents: isViewMode ? "none" : "auto", opacity: isViewMode ? 0.7 : 1 }}>
                                <FileUploader
                                    handleChange={handleVideoFileChange}
                                    name="video"
                                    types={["MP4", "WEBM", "MOV", "AVI"]}
                                    maxSize={videoUploadMaxSizeMb}
                                    disabled={isViewMode}
                                    onTypeError={() => alert('אנא בחר קובץ וידאו בפורמט נתמך (MP4, WEBM, MOV, AVI)')}
                                    onSizeError={() => alert(getVideoTooLargeMessage(videoUploadMaxSizeMb))}
                                >
                                    <Box
                                        sx={{
                                            border: "2px dashed #E2E8F0",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            cursor: isViewMode ? "default" : "pointer",
                                            backgroundColor: "#FAFAFA",
                                            "&:hover": !isViewMode ? {
                                                borderColor: "#57837E",
                                                backgroundColor: "#F8FAFC"
                                            } : {}
                                        }}
                                    >
                                        <Box sx={{
                                            padding: "30px 20px",
                                            textAlign: "center",
                                            paddingBottom: (videoFileName || videoFile) ? "70px" : "30px"
                                        }}>
                                            <VideoIcon sx={{fontSize: "40px", color: "#94A3B8", marginBottom: "8px"}}/>
                                            <Typography sx={{fontSize: "14px", color: "#4F535C", marginBottom: "4px"}}>
                                                גררו ושחררו קובץ וידאו כאן, או
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                disabled={isViewMode}
                                                sx={{
                                                    color: "#4F535C",
                                                    borderColor: "#E2E8F0",
                                                    backgroundColor: "#FFFFFF",
                                                    "&:hover": {
                                                        borderColor: "#57837E",
                                                        backgroundColor: "#F8FAFC"
                                                    }
                                                }}
                                            >
                                                בחרו קובץ
                                            </Button>
                                        </Box>
                                    </Box>
                                </FileUploader>
                                <UploadProgressIndicator
                                    {...videoUploadState}
                                    compact
                                    hideCompression
                                />

                                {(videoFileName || videoFile) && (
                                    <Box sx={{
                                        position: "absolute",
                                        bottom: "15px",
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: "calc(100% - 36px)",
                                        height: "40px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: isViewMode ? "flex-end" : "space-between",
                                        padding: "0 16px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "4px",
                                        boxSizing: "border-box"
                                    }}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                            <VideoIcon sx={{color: "#57837E"}}/>
                                            <Typography sx={{fontSize: "14px", color: "#4F535C"}}>
                                                {videoFileName}
                                            </Typography>
                                        </Box>
                                        {!isViewMode && (
                                            <IconButton size="small" onClick={handleRemoveVideoFile}>
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Thumbnail Extraction/Upload */}
                            <VideoThumbnailExtractor
                                videoFile={videoFile}
                                videoUrl={existingVideoUrl}
                                onThumbnailChange={handleThumbnailExtracted}
                                thumbnailPreview={thumbnailPreview}
                                disabled={isViewMode}
                                initialTime={videoThumbnailTime}
                                showManualUpload={true}
                                aspectRatio="square"
                            />
                        </Box>
                    )}
                </Box>
                )}

                {showPdf && (
                <Box>
                    <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#4F535C",
                        marginBottom: "8px",
                        textAlign: "right"
                    }}>
                        קובץ
                    </Typography>

                    <Box sx={{ position: "relative", pointerEvents: isViewMode ? "none" : "auto", opacity: isViewMode ? 0.7 : 1 }}>
                        <FileUploader
                            handleChange={handleFileChange}
                            name="pdf"
                            types={["PDF"]}
                            maxSize={50}
                            disabled={isViewMode}
                            onTypeError={() => alert('אנא בחר קובץ PDF בלבד')}
                            onSizeError={() => alert('הקובץ גדול מדי. מקסימום 50MB')}
                        >
                            <Box
                                sx={{
                                    border: "2px dashed #E2E8F0",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    cursor: isViewMode ? "default" : "pointer",
                                    backgroundColor: "#FAFAFA",
                                    "&:hover": !isViewMode ? {
                                        borderColor: "#57837E",
                                        backgroundColor: "#F8FAFC"
                                    } : {}
                                }}
                            >
                                <Box sx={{
                                    padding: "40px 20px",
                                    textAlign: "center",
                                    paddingBottom: (pdfFileName || pdfFile) ? "80px" : "40px"
                                }}>
                                    <UploadIcon sx={{fontSize: "48px", color: "#94A3B8", marginBottom: "8px"}}/>
                                    <Typography sx={{fontSize: "14px", color: "#4F535C", marginBottom: "4px"}}>
                                        גררו ושחררו את קובץ ה-PDF כאן, או
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        disabled={isViewMode}
                                        sx={{
                                            color: "#4F535C",
                                            borderColor: "#E2E8F0",
                                            backgroundColor: "#FFFFFF",
                                            "&:hover": {
                                                borderColor: "#57837E",
                                                backgroundColor: "#F8FAFC"
                                            }
                                        }}
                                    >
                                        בחרו קובץ
                                    </Button>
                                </Box>
                            </Box>
                        </FileUploader>

                        {/* File name outside FileUploader */}
                        {(pdfFileName || pdfFile) && (
                            <Box sx={{
                                position: "absolute",
                                bottom: "20px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "calc(100% - 36px)",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isViewMode ? "flex-end" : "space-between",
                                padding: "0 16px",
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: "4px",
                                boxSizing: "border-box"
                            }}>
                                <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                    <FileIcon sx={{color: "#009689"}}/>
                                    <Typography sx={{fontSize: "14px", color: "#4F535C"}}>
                                        {pdfFileName}
                                    </Typography>
                                </Box>
                                {!isViewMode ? (
                                    <IconButton
                                        size="small"
                                        onClick={handleRemoveFile}
                                    >
                                        <CloseIcon fontSize="small"/>
                                    </IconButton>
                                ):(
                                    <Box sx={{
                                        height: "40px",
                                        display: "flex",
                                        flex: "1",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        padding: "0 16px",
                                    }}></Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
                )}

                {/* Nutrition (Recipes): measurement units with per-unit macros */}
                {hasNutrition && (
                    <RecipeNutritionFields
                        labels={labels}
                        macrosConfig={nutritionMacros}
                        disabled={isViewMode}
                        units={units}
                        onUnitsChange={setUnits}
                        required={!isViewMode}
                    />
                )}
            </Box>
        </MyDialog>
    );
};

export default ResourceDialog;
